/**
 * 무선상담 자동완성 계산·저장 계약.
 *
 * 제로노트 상담 화면의 입력 흐름을 PhoneBoard 신청서에 옮길 때 화면과 저장이 같은
 * 숫자를 쓰도록 React 바깥의 순수 함수로 둔다. 금액 단위는 모두 원이다.
 */

export type WirelessCarrier = "SKT" | "KT" | "LGU";
export type WirelessPayType = "support" | "plan";
export type WirelessJoinType = "mnp" | "device_change" | "new_join";
export type WirelessAmountMode = "none" | "all" | "direct";

export interface WirelessCardOption {
  id: string;
  name: string;
  monthlyDiscount: number;
  interestRate: number | null;
}

export interface WirelessUsimOption {
  id: string;
  name: string;
  fee: number;
  chargeType: string;
}

export interface WirelessWelfareOption {
  id: string;
  name: string;
  fixedAmount: number;
  fixedRate: number;
  maxAmount: number;
}

export interface WirelessMembershipOption {
  id: string;
  name: string;
  fee: number;
  chargeType: string;
}

export interface WirelessConsultOptions {
  cards: {
    pre: WirelessCardOption[];
    post: WirelessCardOption[];
  };
  usims: WirelessUsimOption[];
  welfare: WirelessWelfareOption[];
  memberships: WirelessMembershipOption[];
  /** 외부 원천 일부가 실패해도 나머지는 쓸 수 있게 실패 영역만 알린다. */
  unavailable: string[];
}

export interface WirelessSelectedAddon {
  id: string;
  name: string;
  monthlyFee: number;
}

export interface WirelessSelectedInsurance {
  id: string;
  name: string;
  monthlyFee: number;
}

export interface WirelessConsultDraft {
  carrier: WirelessCarrier;
  modelId: string;
  modelName: string;
  releasePrice: number;
  planId: string;
  planName: string;
  planMonthlyPrice: number;
  payType: WirelessPayType;
  joinType: WirelessJoinType;
  consultationDate: string;
  planDiscountMonths: "0" | "12" | "12+12" | "24";
  contractDiscountMonths: "0" | "12" | "24";
  contractDiscount: number;
  publicSubsidy: number;
  extraSubsidyMode: WirelessAmountMode;
  extraSubsidy: number;
  pointDiscount: number;
  preCardId: string;
  preCardName: string;
  preCardMonthlyDiscount: number;
  preCardMonths: number;
  preCardTotalOverride: number | null;
  cashMode: WirelessAmountMode;
  cash: number;
  installmentMonths: number;
  installmentInterestRate: number;
  guaranteeName: string;
  guaranteeFee: number;
  soldierDiscountName: string;
  soldierDiscountEnabled: boolean;
  combineId: string;
  combineName: string;
  combineDiscount: number;
  postCardId: string;
  postCardName: string;
  postCardDiscount: number;
  welfareId: string;
  welfareName: string;
  welfareFixedAmount: number;
  welfareFixedRate: number;
  welfareMaxAmount: number;
  promotionName: string;
  promotionDiscount: number;
  changePlanId: string;
  changePlanName: string;
  changePlanMonthlyPrice: number;
  membershipName: string;
  membershipFee: number;
  membershipChargeType: string;
  usimId: string;
  usimName: string;
  usimFee: number;
  usimChargeType: string;
  insurance: WirelessSelectedInsurance | null;
  addons: WirelessSelectedAddon[];
}

export interface WirelessConsultCalculation {
  publicSubsidy: number;
  extraSubsidy: number;
  preCardTotal: number;
  cash: number;
  installmentPrincipal: number;
  installmentFee: number;
  monthlyDevicePayment: number;
  planDiscount: number;
  contractDiscount: number;
  combineDiscount: number;
  welfareDiscount: number;
  promotionDiscount: number;
  soldierDiscount: number;
  billingCardDiscount: number;
  monthlyPlanPayment: number;
  monthlySeparatePayment: number;
  oneTimePayment: number;
  monthlyTotalPayment: number;
  monthlyGrandTotal: number;
  currentMonthExpected: number;
  changePlanDiscount: number;
  changePlanMonthlyPayment: number;
}

export interface WirelessCombineOption {
  id: string;
  name: string;
  /** 단순 정률 선택지만 기본액을 자동 제안한다. 복합 결합은 화면에서 금액을 직접 적는다. */
  rate?: number;
  fixed?: number;
}

export const WIRELESS_INSTALLMENT_MONTHS = [3, 6, 9, 10, 12, 18, 24, 30, 36, 48] as const;

export const WIRELESS_COMBINE_OPTIONS: Record<WirelessCarrier, WirelessCombineOption[]> = {
  SKT: [
    { id: "", name: "미적용" },
    { id: "SK_12", name: "요즘가족결합" },
    { id: "SK_1", name: "온가족할인 10년미만 10%(BAND계열 0%)", rate: 10 },
    { id: "SK_3", name: "온가족할인 20년미만 20%(BAND계열 0%)", rate: 20 },
    { id: "SK_4", name: "온가족할인 30년미만 30%(BAND계열 10%)", rate: 30 },
    { id: "SK_5", name: "온가족할인 30년이상 50%(BAND계열 30%)", rate: 50 },
    { id: "SK_6", name: "NEW 온가족플랜 (신규가입불가 22.10.08~)" },
    { id: "SK_13", name: "온가족 케이블플랜" },
    { id: "SK_7", name: "온가족프리(대표자) 정액 3,300원", fixed: 3_300 },
    { id: "SK_8", name: "TV플러스(대표자) 최대 2,200원", fixed: 2_200 },
    { id: "SK_10", name: "온가족프리 + TV플러스(대표자)", fixed: 5_500 },
  ],
  KT: [
    { id: "", name: "미적용" },
    { id: "KT_1", name: "총액결합할인(프리미엄포함)" },
    { id: "KT_2", name: "신혼 미리 결합(프로모션)" },
    { id: "KT_4", name: "우리가족 무선결합" },
    { id: "KT_40", name: "친구사이 무선결합" },
    { id: "KT_41", name: "Y끼리 무선결합" },
    { id: "KT_16", name: "정액결합할인" },
    { id: "KT_6", name: "프리미엄싱글결합" },
    { id: "KT_21", name: "케이블 총액결합할인" },
    { id: "KT_11", name: "구 결합 1대(10%, 5G 10%)", rate: 10 },
    { id: "KT_12", name: "구 결합 2대(20%, 5G 15%)", rate: 15 },
    { id: "KT_13", name: "구 결합 3대(30%, 5G 20%)", rate: 20 },
    { id: "KT_14", name: "구 결합 4대(40%, 5G 25%)", rate: 25 },
    { id: "KT_15", name: "구 결합 5대(50%, 5G 30%)", rate: 30 },
    { id: "KT_5", name: "뭉치면올레2(2016년 8월 이후 결합불가)" },
  ],
  LGU: [
    { id: "", name: "미적용" },
    { id: "LG_2", name: "가족무한사랑(무무선)" },
    { id: "LG_31", name: "U+투게더결합" },
    { id: "LG_5", name: "참 쉬운 가족결합(2인)" },
    { id: "LG_6", name: "참 쉬운 가족결합(3인)" },
    { id: "LG_7", name: "참 쉬운 가족결합(4인~10인)" },
    { id: "LG_8", name: "참 쉬운 가족결합(지정할인)" },
    { id: "LG_21", name: "참 쉬운 케이블 가족결합(2인)" },
    { id: "LG_22", name: "참 쉬운 케이블 가족결합(3인)" },
    { id: "LG_23", name: "참 쉬운 케이블 가족결합(4인~10인)" },
    { id: "LG_1", name: "가족무한사랑(유무선)(신규결합불가)" },
    { id: "LG_10", name: "5G 프리미엄 가족할인(신규결합불가)" },
    { id: "LG_3", name: "한방에Home2(신규결합불가)" },
    { id: "LG_4", name: "한방에Yo(신규결합불가)" },
  ],
};

/** 신청서에서 숨기고 자동완성 창·서식 슬롯에서만 쓰는 값. */
export const WIRELESS_AUTOFILL_FIELD_META = [
  ["consultationDate", "상담일", "date"],
  ["autofillPayType", "지원금(약정유형)", "text"],
  ["autofillPlanDiscountMonths", "선택약정 기간", "text"],
  ["autofillPointDiscount", "포인트할인", "number"],
  ["autofillPreCardName", "선할인카드", "text"],
  ["autofillPreCardAmount", "선할인카드 할인총액", "number"],
  ["autofillInstallmentInterestRate", "할부이자율", "number"],
  ["autofillInstallmentFee", "할부수수료", "number"],
  ["autofillMonthlyDevicePayment", "단말 월 할부금", "number"],
  ["autofillGuaranteeName", "중고보장 프로그램", "text"],
  ["autofillGuaranteeFee", "중고보장 프로그램 이용료", "number"],
  ["autofillPlanMonthlyPrice", "요금제 금액", "number"],
  ["autofillPlanDiscount", "선택약정", "number"],
  ["autofillContractDiscount", "약정할인", "number"],
  ["autofillSoldierDiscount", "0히어로 할인제도", "number"],
  ["autofillCombineName", "결합할인", "text"],
  ["autofillCombineDiscount", "결합할인 금액", "number"],
  ["autofillBillingCardName", "청구할인카드", "text"],
  ["autofillBillingCardDiscount", "청구할인카드 할인액", "number"],
  ["autofillPromotionName", "프로모션할인", "text"],
  ["autofillPromotionDiscount", "프로모션할인 금액", "number"],
  ["autofillMonthlyPlanPayment", "월요금", "number"],
  ["autofillMonthlyTotalPayment", "월별 청구금액", "number"],
  ["autofillCurrentMonthPayment", "당월 예상금액", "number"],
  ["autofillChangePlanName", "변경요금제", "text"],
  ["autofillChangePlanPayment", "변경 청구금액", "number"],
  ["autofillMembershipName", "가입비", "text"],
  ["autofillMembershipFee", "가입비 금액", "number"],
  ["autofillUsimName", "유심", "text"],
  ["autofillUsimFee", "유심비", "number"],
  ["autofillAddonMonthlyFee", "부가서비스 월요금", "number"],
  ["autofillInsuranceMonthlyFee", "휴대폰보험 월요금", "number"],
  ["autofillSeparatePayment", "별도 청구금액", "number"],
] as const;

export const WIRELESS_AUTOFILL_FIELD_KEYS = WIRELESS_AUTOFILL_FIELD_META.map(
  ([key]) => key,
);

export function createWirelessConsultDraft(
  today = new Date(),
): WirelessConsultDraft {
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  return {
    carrier: "SKT",
    modelId: "",
    modelName: "",
    releasePrice: 0,
    planId: "",
    planName: "",
    planMonthlyPrice: 0,
    payType: "support",
    joinType: "mnp",
    consultationDate: `${y}-${m}-${d}`,
    planDiscountMonths: "24",
    contractDiscountMonths: "24",
    contractDiscount: 0,
    publicSubsidy: 0,
    extraSubsidyMode: "none",
    extraSubsidy: 0,
    pointDiscount: 0,
    preCardId: "",
    preCardName: "",
    preCardMonthlyDiscount: 0,
    preCardMonths: 24,
    preCardTotalOverride: null,
    cashMode: "none",
    cash: 0,
    installmentMonths: 24,
    installmentInterestRate: 5.9,
    guaranteeName: "",
    guaranteeFee: 0,
    soldierDiscountName: "",
    soldierDiscountEnabled: false,
    combineId: "",
    combineName: "",
    combineDiscount: 0,
    postCardId: "",
    postCardName: "",
    postCardDiscount: 0,
    welfareId: "",
    welfareName: "",
    welfareFixedAmount: 0,
    welfareFixedRate: 0,
    welfareMaxAmount: 0,
    promotionName: "",
    promotionDiscount: 0,
    changePlanId: "",
    changePlanName: "",
    changePlanMonthlyPrice: 0,
    membershipName: "면제",
    membershipFee: 0,
    membershipChargeType: "EXEMPTION",
    usimId: "",
    usimName: "미적용",
    usimFee: 0,
    usimChargeType: "NONE",
    insurance: null,
    addons: [],
  };
}

const money = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
};

const roundTen = (value: number): number => Math.round(value / 10) * 10;

function planDiscount(monthlyPrice: number, enabled: boolean): number {
  return enabled ? roundTen(monthlyPrice * 0.25) : 0;
}

function welfareDiscount(
  draft: WirelessConsultDraft,
  baseAfterEarlierDiscounts: number,
): number {
  if (!draft.welfareName) return 0;
  const fixed = money(draft.welfareFixedAmount);
  const rate = money(draft.welfareFixedRate) / 100;
  const variableBase = Math.max(0, baseAfterEarlierDiscounts - fixed);
  const calculated = Math.floor(fixed + variableBase * rate);
  const max = money(draft.welfareMaxAmount);
  return max > 0 ? Math.min(calculated, max) : calculated;
}

function monthRatio(dateText: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateText);
  if (!match) return 1;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const last = new Date(year, month, 0).getDate();
  if (!Number.isFinite(day) || day < 1 || day > last) return 1;
  return (last - day + 1) / last;
}

export function calculateWirelessConsult(
  draft: WirelessConsultDraft,
): WirelessConsultCalculation {
  const releasePrice = money(draft.releasePrice);
  const publicSubsidy = draft.payType === "support" ? money(draft.publicSubsidy) : 0;
  const pointDiscount = money(draft.pointDiscount);
  const preCardTotal = Math.min(
    releasePrice,
    money(
      draft.preCardTotalOverride ??
        draft.preCardMonthlyDiscount * draft.preCardMonths,
    ),
  );
  const beforeExtra = Math.max(
    0,
    releasePrice - publicSubsidy - pointDiscount - preCardTotal,
  );
  const extraSubsidy =
    draft.extraSubsidyMode === "all"
      ? beforeExtra
      : draft.extraSubsidyMode === "direct"
        ? Math.min(beforeExtra, money(draft.extraSubsidy))
        : 0;
  const beforeCash = Math.max(0, beforeExtra - extraSubsidy);
  const cash =
    draft.cashMode === "all"
      ? beforeCash
      : draft.cashMode === "direct"
        ? Math.min(beforeCash, money(draft.cash))
        : 0;
  const installmentPrincipal = Math.max(0, beforeCash - cash);

  const months = Math.max(1, money(draft.installmentMonths));
  const annualInterestRate = Number.isFinite(Number(draft.installmentInterestRate))
    ? Math.max(0, Number(draft.installmentInterestRate))
    : 0;
  const monthlyRate = annualInterestRate / 100 / 12;
  let monthlyDevicePayment = 0;
  if (installmentPrincipal > 0) {
    monthlyDevicePayment =
      monthlyRate > 0
        ? roundTen(
            (installmentPrincipal * monthlyRate * (1 + monthlyRate) ** months) /
              ((1 + monthlyRate) ** months - 1),
          )
        : roundTen(installmentPrincipal / months);
  }
  const installmentFee = Math.max(
    0,
    monthlyDevicePayment * months - installmentPrincipal,
  );

  const basicPlan = money(draft.planMonthlyPrice);
  const planDc = planDiscount(
    basicPlan,
    draft.payType === "plan" && draft.planDiscountMonths !== "0",
  );
  const contractDc = Math.min(basicPlan, money(draft.contractDiscount));
  const combineDc = Math.min(basicPlan, money(draft.combineDiscount));
  const promotionDc = Math.min(basicPlan, money(draft.promotionDiscount));
  const welfareBase = Math.max(
    0,
    basicPlan - planDc - contractDc - combineDc - promotionDc,
  );
  const welfareDc = Math.min(
    welfareBase,
    welfareDiscount(draft, welfareBase),
  );
  const beforeSoldier = Math.max(0, welfareBase - welfareDc);
  const soldierDc = draft.soldierDiscountEnabled
    ? roundTen(beforeSoldier * 0.2)
    : 0;
  const guaranteeFee = money(draft.guaranteeFee);
  const beforeBillingCard = Math.max(
    0,
    beforeSoldier - soldierDc + guaranteeFee,
  );
  const billingCardDiscount = Math.min(
    beforeBillingCard,
    money(draft.postCardDiscount),
  );
  const monthlyPlanPayment = Math.max(
    0,
    beforeBillingCard - billingCardDiscount,
  );

  const addonFee = draft.addons.reduce(
    (sum, item) => sum + money(item.monthlyFee),
    0,
  );
  const insuranceFee = money(draft.insurance?.monthlyFee ?? 0);
  const monthlySeparatePayment = addonFee + insuranceFee;
  const membershipFee = money(draft.membershipFee);
  const usimFee = money(draft.usimFee);
  const oneTimePayment =
    (draft.membershipChargeType === "DEPUTY" ? 0 : membershipFee) +
    (draft.usimChargeType === "BILL" ? 0 : usimFee);
  const monthlyTotalPayment = monthlyDevicePayment + monthlyPlanPayment;
  const monthlyGrandTotal = monthlyTotalPayment + monthlySeparatePayment;
  const ratio = monthRatio(draft.consultationDate);
  const currentDevice =
    draft.carrier === "SKT"
      ? Math.floor(monthlyDevicePayment * ratio)
      : monthlyDevicePayment;
  const currentPlan = Math.floor(
    Math.max(0, beforeBillingCard) * ratio,
  );
  const currentMonthExpected = Math.max(
    0,
    currentDevice + currentPlan - billingCardDiscount,
  );

  const changeBasic = money(draft.changePlanMonthlyPrice);
  const changePlanDiscount = planDiscount(
    changeBasic,
    draft.payType === "plan" && draft.planDiscountMonths !== "0",
  );
  const changePlanMonthlyPayment = Math.max(
    0,
    changeBasic -
      changePlanDiscount -
      contractDc -
      combineDc -
      promotionDc -
      welfareDc -
      soldierDc -
      billingCardDiscount +
      guaranteeFee,
  );

  return {
    publicSubsidy,
    extraSubsidy,
    preCardTotal,
    cash,
    installmentPrincipal,
    installmentFee,
    monthlyDevicePayment,
    planDiscount: planDc,
    contractDiscount: contractDc,
    combineDiscount: combineDc,
    welfareDiscount: welfareDc,
    promotionDiscount: promotionDc,
    soldierDiscount: soldierDc,
    billingCardDiscount,
    monthlyPlanPayment,
    monthlySeparatePayment,
    oneTimePayment,
    monthlyTotalPayment,
    monthlyGrandTotal,
    currentMonthExpected,
    changePlanDiscount,
    changePlanMonthlyPayment,
  };
}

export function wirelessCarrierFormValue(carrier: WirelessCarrier): string {
  return carrier === "LGU" ? "LGU+" : carrier;
}

export function wirelessJoinFormValue(joinType: WirelessJoinType): string {
  if (joinType === "mnp") return "number_port";
  if (joinType === "device_change") return "device_change";
  return "new";
}

export function wirelessContractType(draft: WirelessConsultDraft): string {
  if (draft.payType === "support") return "공시 / 24개월";
  return draft.planDiscountMonths === "12"
    ? "선약 / 12개월"
    : "선약 / 24개월";
}

export function wirelessSimPurchaseType(draft: WirelessConsultDraft): string {
  if (!draft.usimId || draft.usimFee === 0) return "기존유심";
  if (/eSIM|이심/i.test(draft.usimName)) return "이심(가상유심)";
  if (draft.usimChargeType === "BILL") return "후납(다음달 요금합산)";
  if (/대납/.test(draft.usimName)) return "무상유심(대리점 제공)";
  return "선납";
}

export function wirelessAutofillRetailerPatch(
  draft: WirelessConsultDraft,
  calc = calculateWirelessConsult(draft),
): Record<string, unknown> {
  const addonNames = draft.addons.map((item) => item.name);
  const insuranceNames = draft.insurance ? [draft.insurance.name] : [];
  return {
    consultationDate: draft.consultationDate,
    model: draft.modelName,
    plan: draft.planName,
    contractType: wirelessContractType(draft),
    releasePrice: String(money(draft.releasePrice)),
    publicSubsidy: String(calc.publicSubsidy),
    extraSubsidy: String(calc.extraSubsidy),
    freeInstallmentPayment: String(calc.cash),
    installmentPrincipal: String(calc.installmentPrincipal),
    installmentMonths: String(draft.installmentMonths),
    welfareDiscount: draft.welfareName,
    simPurchaseType: wirelessSimPurchaseType(draft),
    insurance: insuranceNames,
    addedAddons: [...addonNames, ...insuranceNames],
    autofillPayType: draft.payType === "support" ? "단말지원" : "선택약정",
    autofillPlanDiscountMonths:
      draft.planDiscountMonths === "0"
        ? "무약정"
        : `${draft.planDiscountMonths}개월`,
    autofillPointDiscount: String(money(draft.pointDiscount)),
    autofillPreCardName: draft.preCardName,
    autofillPreCardAmount: String(calc.preCardTotal),
    autofillInstallmentInterestRate: String(draft.installmentInterestRate),
    autofillInstallmentFee: String(calc.installmentFee),
    autofillMonthlyDevicePayment: String(calc.monthlyDevicePayment),
    autofillGuaranteeName: draft.guaranteeName,
    autofillGuaranteeFee: String(money(draft.guaranteeFee)),
    autofillPlanMonthlyPrice: String(money(draft.planMonthlyPrice)),
    autofillPlanDiscount: String(calc.planDiscount),
    autofillContractDiscount: String(calc.contractDiscount),
    autofillSoldierDiscount: String(calc.soldierDiscount),
    autofillCombineName: draft.combineName,
    autofillCombineDiscount: String(calc.combineDiscount),
    autofillBillingCardName: draft.postCardName,
    autofillBillingCardDiscount: String(calc.billingCardDiscount),
    autofillPromotionName: draft.promotionName,
    autofillPromotionDiscount: String(calc.promotionDiscount),
    autofillMonthlyPlanPayment: String(calc.monthlyPlanPayment),
    autofillMonthlyTotalPayment: String(calc.monthlyTotalPayment),
    autofillCurrentMonthPayment: String(calc.currentMonthExpected),
    autofillChangePlanName: draft.changePlanName,
    autofillChangePlanPayment: String(calc.changePlanMonthlyPayment),
    autofillMembershipName: draft.membershipName,
    autofillMembershipFee: String(money(draft.membershipFee)),
    autofillUsimName: draft.usimName,
    autofillUsimFee: String(money(draft.usimFee)),
    autofillAddonMonthlyFee: String(
      draft.addons.reduce((sum, item) => sum + money(item.monthlyFee), 0),
    ),
    autofillInsuranceMonthlyFee: String(
      money(draft.insurance?.monthlyFee ?? 0),
    ),
    autofillSeparatePayment: String(calc.monthlySeparatePayment),
  };
}

export function wirelessAutofillCustomerPatch(
  draft: WirelessConsultDraft,
): Record<string, unknown> {
  return {
    desiredCarrier: wirelessCarrierFormValue(draft.carrier),
    joinType: wirelessJoinFormValue(draft.joinType),
    desiredModel: draft.modelName,
    desiredPlan: draft.planName,
  };
}
